import { getAuthorizedExternalOrigin } from "@/components/auth/AuthLoginRedirect";
import {useGetOAuthClient} from "@/hooks/useGetOAuthClient";
import { IStore } from "@/redux/defaultStore";
import {useCallback} from "react";
import { useSelector } from "react-redux";

const formatUriIfTestCallback = (uri: string): string => {
    const url = new URL(uri);
    if (url.pathname === "/cdn_devourgo/documentation/oauth-client-code-steps.html") {
        url.searchParams.set("redirect_uri", `${import.meta.env.VITE_WEB_URL}/external/oauth/complete`);
    }
    return url.toString();
};

export const useControlOauthFlow = (oAuthClientId?: string) => {
    const {data: oAuthClient} = useGetOAuthClient(oAuthClientId);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    const redirectUri = useCallback((referralCode?: string) => {
        if (!oAuthClient) {
            return "/";
        }
        const url = new URL(`${import.meta.env.VITE_BACKEND_REST_URL}/oauth/init`);
        url.searchParams.set("client_id", oAuthClient.id);
        url.searchParams.set("redirect_uri", formatUriIfTestCallback(oAuthClient.redirectUris[0]));
        url.searchParams.set("response_type", "code");
        url.searchParams.set("state", "test-account-linked-state");
        if (referralCode) {
            url.searchParams.set("ref", referralCode);
        }
        return url.toString();
    }, [oAuthClient]);

    const applyOAuthState = (newClientId: string) => {
        if (!newClientId) {
            throw new Error("OAuth client not found.");
        }
        const currentOauthState = window.localStorage.getItem("oauthState");
        const oauthStateDate = new Date(parseInt(currentOauthState || "0", 10));
        const now = new Date();
        const diff = now.getTime() - oauthStateDate.getTime(); // oauth state active for one hour
        if (diff > 60 * 1000 * 60) {
            window.localStorage.setItem("oauthClientId", newClientId);
            window.localStorage.setItem("oauthState", `${now.getTime()}`);
        }
    };

    const handleOauthStart = (referralCode?: string, utmSource?: string) => {
        if (!oAuthClient) {
            throw new Error("OAuth client not found.");
        }
        applyOAuthState(oAuthClient.id);
        if (utmSource) {
            window.localStorage.setItem("oauthUtmSource", utmSource);
        }
        const popupWidth = 450;
        const popupHeight = 675;
        const left = Math.round(window.screen.width * 0.05);
        const top = Math.round(window.screen.height * 0.12);
        window.open(
            redirectUri(referralCode),
            "oauthPopup",
            `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`,
        );
    };

    const handleOauthCompletion = useCallback(() => {
        window.localStorage.removeItem("oauthState");
        window.localStorage.removeItem("oauthClientId");
        window.localStorage.removeItem("oauthUtmSource");
        if (window.opener) {
            const authorizedExternalOrigin = getAuthorizedExternalOrigin(window.opener.origin);
            const baseUrl = authorizedExternalOrigin || import.meta.env.VITE_WEB_URL;
            window.opener.postMessage({
                type: "devourgo-external-login",
                authToken: fullToken,
            }, baseUrl);
        }
        setTimeout(() => {
            window.close();
        }, 5000);
    }, [fullToken]);

    const isOauthFlowActive = (): boolean => {
        const currentOauthState = window.localStorage.getItem("oauthState");
        const oauthStateDate = new Date(parseInt(currentOauthState || "0", 10));
        const now = new Date();
        const diff = now.getTime() - oauthStateDate.getTime();
        return diff < 60 * 1000 * 60;
    };

    if (import.meta.env.DEV) {
        (window as unknown as any).isOauthFlowActive = isOauthFlowActive;
    }

    const navigateToOauthCallback = useCallback(() => {
        window.location.href = redirectUri();
    }, [redirectUri]);


    return {
        handleOauthStart,
        isOauthFlowActive,
        navigateToOauthCallback,
        handleOauthCompletion,
        applyOAuthState,
        oAuthClient,
    };
};

